import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useForm, type DefaultValues } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { ArrowLeft, ArrowRight, Check } from 'lucide-react';

import { Alert } from '@/components/ui/Alert';
import { Button } from '@/components/ui/Button';
import { Stepper } from '@/components/ui/Stepper';
import {
  BOARD_LABEL,
  GENDER_TYPE_LABEL,
  MANAGEMENT_LABEL,
  PAYMENT_STATUS_LABEL,
  RESIDENTIAL_LABEL,
  instituteLevelLabel,
  type PaymentStatus,
} from '@/constants/institute';
import { ApiRequestError } from '@/lib/apiClient';
import { formatCurrency, formatDate } from '@/lib/format';
import { DeckPageHeader } from '../../components/DeckPageHeader';
import { sharedMobilesWarning } from '../sharedMobiles';
import { institutesApi } from '../api/institutes.api';
import {
  STEP_FIELDS,
  WIZARD_STEPS,
  instituteSchema,
  withoutSchoolOnlyFields,
  type InstituteFormValues,
} from '../schemas/institute.schema';
import {
  AcademicStep,
  ContactStep,
  IdentityStep,
  PeopleStep,
} from '../components/InstituteWizardSteps';
import { useAuthStore } from '@/features/auth/store/auth.store';
import { useLiveFormErrors } from '@/hooks/useLiveFormErrors';
import { useSessionDraft } from '@/hooks/useSessionDraft';
import { SERVER_ERROR_TYPE } from '@/lib/forms';

/** What the wizard opens on, and what a restored draft is laid over. */
const EMPTY_INSTITUTE_FORM: DefaultValues<InstituteFormValues> = {
  legalName: '',
  displayName: '',
  // Most customers are institutes, so the form opens on the common case.
  // Choosing College hides the board and level questions below.
  institutionType: 'SCHOOL',
  board: 'CBSE',
  managementType: 'PRIVATE_UNAIDED',
  schoolLevel: 'K12',
  // The common case for a college, as K-12 is for a school; only read when
  // College is chosen.
  collegeLevel: 'UNDERGRADUATE',
  lowestClass: 'NURSERY',
  highestClass: 'XII',
  genderType: 'CO_ED',
  residentialType: 'DAY',
  mediumOfInstruction: ['ENGLISH'],
  streams: [],
  addressLine1: '',
  city: '',
  district: '',
  pincode: '',
  latitude: '',
  longitude: '',
  phone: '',
  email: '',
  principalName: '',
  principalPhone: '',
  principalEmail: '',
  adminContactName: '',
  adminContactPhone: '',
  adminContactEmail: '',
  // Blank until the fee is agreed, and nothing is owed as paid until
  // somebody says it was received.
  subscriptionAmount: '',
  paymentStatus: 'UNPAID',
  subscriptionDueDate: '',
};

/** What is kept through a refresh: the answers so far, and the step reached. */
interface InstituteDraft {
  values: Partial<InstituteFormValues>;
  step: number;
}

export function CreateInstitutePage() {
  const navigate = useNavigate();
  const userUuid = useAuthStore((state) => state.user?.uuid);
  const draft = useSessionDraft<InstituteDraft>(userUuid ? `create-institute:${userUuid}` : null);
  // Clamped, so a draft from before a step was removed cannot point past the end.
  const [step, setStep] = useState(() =>
    Math.min(Math.max(draft.initial?.step ?? 0, 0), WIZARD_STEPS.length - 1),
  );
  const [formError, setFormError] = useState<string | null>(null);

  const form = useForm<InstituteFormValues>({
    // `raw`: submit receives what was typed, not zod's output, so the single
    // parse in `onSubmit` is the only one. Parsing the output a second time
    // failed on a blank due date (already `null`) and turned a cleared fee and
    // pin into zeros.
    resolver: zodResolver(instituteSchema, undefined, { raw: true }),
    // Checked while typing as well as on leaving a field. On leaving alone, a
    // message stayed up after the value was corrected, and whether it showed
    // at all depended on which field was clicked next.
    mode: 'all',
    // A refreshed page picks up where it was. Spread over the blank form so a
    // draft saved before a field was added still fills every input.
    defaultValues: { ...EMPTY_INSTITUTE_FORM, ...draft.initial?.values },
  });
  useLiveFormErrors(form);

  const {
    register,
    control,
    watch,
    setValue,
    setError,
    trigger,
    handleSubmit,
    getValues,
    formState: { errors, isSubmitting },
  } = form;

  const { save: saveDraft, clear: clearDraft } = draft;

  // Saved on every change and every step move, so a refresh loses nothing.
  useEffect(() => {
    saveDraft({ values: getValues(), step });
    const subscription = watch((current) =>
      saveDraft({ values: current as Partial<InstituteFormValues>, step }),
    );
    return () => subscription.unsubscribe();
  }, [watch, getValues, saveDraft, step]);

  const isReviewStep = step === WIZARD_STEPS.length - 1;
  const values = watch();

  /** Validates only the current step's fields before moving on. */
  async function goNext() {
    setFormError(null);
    const fields = STEP_FIELDS[step];
    if (fields) {
      // Focused, so a refusal is seen: on a long step the offending field is
      // often scrolled out of view, and "Continue" otherwise looked dead.
      const isValid = await trigger(fields as unknown as (keyof InstituteFormValues)[], {
        shouldFocus: true,
      });
      if (!isValid) return;
    }
    setStep((current) => Math.min(current + 1, WIZARD_STEPS.length - 1));
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }

  function goBack() {
    setFormError(null);
    setStep((current) => Math.max(current - 1, 0));
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }

  const onSubmit = handleSubmit(async (values) => {
    setFormError(null);
    try {
      // The resolver validates but hands back the *input* shape. Parsing here
      // applies zod's coercions — "500" → 500, "" → undefined — so the API gets
      // the payload type it declares rather than raw form strings.
      const institute = await institutesApi.create(
        withoutSchoolOnlyFields(instituteSchema.parse(values)),
      );
      // Created — the draft has done its job and must not reopen as a duplicate.
      clearDraft();
      navigate(`/platform/institutes/${institute.uuid}`, {
        replace: true,
        state: { justCreated: true },
      });
    } catch (error) {
      if (error instanceof ApiRequestError) {
        // Server-side field errors land back on the step that owns them.
        const fieldEntries = Object.entries(error.fieldMap);
        if (fieldEntries.length > 0) {
          for (const [field, message] of fieldEntries) {
            setError(field as keyof InstituteFormValues, { type: SERVER_ERROR_TYPE, message });
          }
          const firstBadStep = STEP_FIELDS.findIndex((fields) =>
            fieldEntries.some(([field]) => (fields as readonly string[]).includes(field)),
          );
          if (firstBadStep >= 0) setStep(firstBadStep);
        }
        setFormError(error.message);
        return;
      }
      setFormError('Something went wrong. Please try again.');
    }
  });

  const stepProps = { register, errors, control, watch, setValue };

  return (
    <>
      {/* The rail rides in the banner rather than in a card of its own: where
          you are in a five-step form is part of the heading, and it saves the
          reader a panel between the title and the fields they came to fill. */}
      <DeckPageHeader
        eyebrow="New customer"
        title="Add an institute"
        description="Creates the institute and the first administrator account."
        backTo={{ to: '/platform/institutes', label: 'Back to institutes' }}
        actions={
          <span className="rounded-pill border border-line bg-surface px-3 py-1.5 text-xs font-[560] text-ink-2">
            Step {step + 1} of {WIZARD_STEPS.length}
          </span>
        }
      >
        <div className="custom-scrollbar overflow-x-auto pb-1">
          <Stepper
            steps={WIZARD_STEPS}
            currentStep={step}
            onStepClick={(index) => setStep(index)}
          />
        </div>
      </DeckPageHeader>

      {formError && (
        <Alert tone="critical" title="Could not create the institute">
          {formError}
        </Alert>
      )}

      <form onSubmit={onSubmit} noValidate className="flex flex-col gap-6">
        <div className="card p-5 lg:p-6">
          {step === 0 && <IdentityStep {...stepProps} />}
          {step === 1 && <AcademicStep {...stepProps} />}
          {step === 2 && <ContactStep {...stepProps} />}
          {step === 3 && <PeopleStep {...stepProps} warnSharedMobiles />}
          {isReviewStep && <ReviewStep values={values} onEditStep={setStep} />}
        </div>

        {/* ---------------- wizard controls ----------------
            A bar of its own rather than buttons loose on the page: it is the
            only thing under a very long form, and it should read as the end of
            the form rather than as something that fell off it. Pinned to the
            bottom of the viewport from `sm` up, so "Continue" is reachable on a
            step with thirty fields without scrolling to the foot of them. */}
        <div className="sticky bottom-4 z-10 flex items-center justify-between gap-3 rounded-card border border-line bg-surface p-3 shadow-card">
          <Button
            variant="ghost"
            onClick={goBack}
            disabled={step === 0 || isSubmitting}
            leftIcon={<ArrowLeft className="h-4 w-4" />}
          >
            Back
          </Button>

          <div className="flex items-center gap-2">
            {/* Distinct keys are load-bearing. Without them React reuses the one
                <button> and flips its type to "submit" while the Continue click
                that reached the review step is still being dispatched — the
                browser then submits the form, creating the institute before the
                review was ever read. */}
            {isReviewStep ? (
              <Button
                key="create"
                type="submit"
                size="lg"
                isLoading={isSubmitting}
                leftIcon={<Check className="h-4 w-4" />}
              >
                {isSubmitting ? 'Creating…' : 'Create institute'}
              </Button>
            ) : (
              <Button
                key="continue"
                size="lg"
                onClick={goNext}
                rightIcon={<ArrowRight className="h-4 w-4" />}
              >
                Continue
              </Button>
            )}
          </div>
        </div>
      </form>
    </>
  );
}

// --- review -----------------------------------------------------------------

interface ReviewStepProps {
  values: InstituteFormValues;
  onEditStep: (step: number) => void;
}

/**
 * Last look before anything is written. Every group links back to the step that
 * owns it, so a correction is one click rather than a walk back through the
 * wizard.
 */
function ReviewStep({ values, onEditStep }: ReviewStepProps) {
  const isCollege = values.institutionType === 'COLLEGE';
  // Every answer the institute is created with is shown, defaults included: a
  // field the reader never opened is still saved, so it must be seen here.
  const groups: { title: string; step: number; rows: [string, string][] }[] = [
    {
      title: 'Identity & recognition',
      step: 0,
      rows: [
        ['Institute', values.displayName || '—'],
        ['Registered name', values.legalName || '—'],
        // Assigned by the server when the institute is saved.
        ['Code', 'Assigned automatically on save'],
        ['Subdomain', values.subdomain || 'Not set'],
        ['UDISE+', values.udiseCode || 'Not provided'],
        // A college has no board; its hidden default is not sent.
        ['Board', isCollege ? 'Not applicable' : values.board ? BOARD_LABEL[values.board] : '—'],
        ['Management', values.managementType ? MANAGEMENT_LABEL[values.managementType] : '—'],
      ],
    },
    {
      title: 'Academic profile',
      step: 1,
      rows: [
        ['Level', instituteLevelLabel(values)],
        ['Classes', `${values.lowestClass} – ${values.highestClass}`],
        ['Intake', values.genderType ? GENDER_TYPE_LABEL[values.genderType] : '—'],
        ['Type', values.residentialType ? RESIDENTIAL_LABEL[values.residentialType] : '—'],
        ['Medium', (values.mediumOfInstruction ?? []).join(', ') || '—'],
        ['Streams', (values.streams ?? []).join(', ') || 'None'],
      ],
    },
    {
      title: 'Address & contact',
      step: 2,
      rows: [
        ['Address', [values.addressLine1, values.locality, values.city].filter(Boolean).join(', ')],
        ['State', `${values.state ?? '—'} — ${values.pincode ?? ''}`],
        ['Phone', values.phone || '—'],
        ['Email', values.email || '—'],
      ],
    },
    {
      title: 'People',
      step: 3,
      rows: [
        ['Principal', values.principalName || '—'],
        ['Principal mobile', values.principalPhone || '—'],
        ['Administrator', values.adminContactName || '—'],
        // The admin signs in with these two, so they are the ones worth a last look.
        ['Admin mobile', values.adminContactPhone || '—'],
        ['Admin email (invite goes here)', values.adminContactEmail || '—'],
        ['Licence fee', formatCurrency(values.subscriptionAmount)],
        ['Payment', PAYMENT_STATUS_LABEL[(values.paymentStatus ?? 'UNPAID') as PaymentStatus]],
        ['Due date', formatDate(values.subscriptionDueDate)],
      ],
    },
  ];

  const sharedMobiles = sharedMobilesWarning(values);

  return (
    <div className="flex flex-col gap-6">
      <div>
        <h2 className="text-base">Review before creating</h2>
        <p className="mt-0.5 text-sm text-ink-2">
          The institute is created with status <strong className="text-ink">Pending setup</strong>,
          and {values.adminContactEmail || 'the administrator'} receives a one-time link to choose a
          password.
        </p>
      </div>

      {sharedMobiles && (
        <Alert tone="warning" variant="note" title="Same mobile number">
          {sharedMobiles}
        </Alert>
      )}

      {groups.map((group) => (
        <section key={group.title} className="rounded-card border border-line">
          <header className="flex items-center justify-between gap-3 border-b border-line bg-surface-2 px-4 py-2.5">
            <h3 className="lbl">{group.title}</h3>
            <button
              type="button"
              onClick={() => onEditStep(group.step)}
              className="text-xs font-[560] text-accent transition-colors hover:underline"
            >
              Edit
            </button>
          </header>

          <dl className="grid grid-cols-1 gap-x-6 gap-y-2.5 px-4 py-3.5 sm:grid-cols-2">
            {group.rows.map(([label, value]) => (
              <div key={label} className="flex items-baseline justify-between gap-3 text-sm">
                <dt className="shrink-0 text-ink-3">{label}</dt>
                <dd className="min-w-0 truncate text-right font-[560] text-ink">{value}</dd>
              </div>
            ))}
          </dl>
        </section>
      ))}
    </div>
  );
}
